import {
  getFilterSubTypeTitle,
  getFilterTitle,
} from "helpers/utility/queryFilterHelpers";
import { FilterMenuItemType, FilterSubType, Position } from "types";
import { getTextWidth } from "helpers/utility/utilities";
import { useEffect, useRef, useState } from "react";
import XIcon from "assets/icons/XIcon";
import classNames from "classnames";

import styles from "./style.module.scss";

export const TAB_HEIGHT_WITH_OFFSET = 28;

export interface QueryTabProps {
  labels: string[];
  operator: string;
  isRange?: boolean;
  noColon?: boolean;
  icon?: JSX.Element;
  type: FilterMenuItemType;
  subType?: FilterSubType | null;
  onRemove: () => void;
  onClick?: (p: Position) => void;
  id: string;
}

export default function QueryTab({
  icon,
  type,
  labels,
  operator,
  isRange = false,
  noColon = false,
  subType = null,
  onClick,
  onRemove,
  id,
}: QueryTabProps) {
  const MAX_LABELS = 3;
  const isOverflowing = labels.length > MAX_LABELS;
  const labelsRef = useRef<any>(null);
  const [modalPosition, setModalPosition] = useState<Position | null>(null);

  const joiningOperator = isRange ? "-" : ",";
  const labelsText = labels.join(joiningOperator);

  const queryTabRef = useRef<HTMLDivElement>(null);

  const queryTabClassnames = classNames(styles.queryTab, {
    [styles.noPointer]: !onClick,
  });
  const labelsClassnames = classNames(styles.labels, {
    [styles.overflow]: isOverflowing,
  });

  const truncateLabel = (label: string | number) => {
    if (typeof label === "number") return label;

    // check for character limit of 21
    if (label.length > 21) {
      return `...${label.slice(-18)}`;
    }

    const lastDotIndex = label.lastIndexOf(".");
    if (lastDotIndex > 0) {
      const namePart = label.slice(0, lastDotIndex);
      const extension = label.slice(lastDotIndex);
      return namePart.length > 8
        ? `...${namePart.slice(-8)}${extension}`
        : label;
    }
    return label;
  };

  const getOperatorText = () => {
    let subText = "";

    if (subType) {
      subText = ` ${getFilterSubTypeTitle(subType)}:`;
    } else if (!noColon) {
      subText = ":";
    }

    return operator + subText;
  };

  const displayLabels = labels.map(truncateLabel).join(joiningOperator);

  const handleClick = () => {
    if (modalPosition && onClick) {
      onClick(modalPosition);
    }
  };

  useEffect(() => {
    if (isOverflowing && labelsRef.current) {
      let totalWidth = labels.slice(0, 3).reduce((width, label) => {
        const trucatedLabel = truncateLabel(label) as string;
        return width + getTextWidth(trucatedLabel, "14px");
      }, 0);

      labelsRef.current.style.setProperty(
        "--maxuimum-width",
        `${totalWidth + 25}px`
      );
    }
  }, [labels, isOverflowing]);

  useEffect(() => {
    const tab = queryTabRef.current;

    if (tab) {
      const rect = tab.getBoundingClientRect();
      setModalPosition({
        top: rect.top + TAB_HEIGHT_WITH_OFFSET,
        left: rect.left,
      });
    }
  }, [labels]);

  return (
    <div
      className={queryTabClassnames}
      data-testid="query-tab"
      data-query-id={id}
      ref={queryTabRef}
      onClick={handleClick}
    >
      {icon && <div className={styles.icon}>{icon}</div>}
      <div className={styles.textContainer}>
        <div className={styles.textOusideLabels}>
          <span className={styles.text}>{getFilterTitle(type)}</span>
          <span
            className={styles.operator}
            data-testid="query-tab-operator-text"
          >
            {getOperatorText()}
          </span>
        </div>
        <div
          data-testid="query-tab-labels-text"
          className={labelsClassnames}
          title={labelsText}
          ref={labelsRef}
        >
          {displayLabels}
        </div>
      </div>
      <div
        data-testid="query-tab-close-btn"
        className={styles.closeBtn}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
      >
        <XIcon />
      </div>
    </div>
  );
}
